import { lazy, Suspense } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { SuspenseSpin } from '@shared/styled/spin'

import { AppLayout } from '@modules/layout/containers'
import { CashRegisterStatusProvider } from '@modules/cash-register/context/status'
import { SimilarUsers } from '@modules/appointments/containers'
import { useAuth } from '@modules/me/hooks'

// Page Routers
const StatisticsRouter = lazy(() => import('../modules/statistics/router/page.router'))
const StatusRouter = lazy(() => import('../modules/status/router/page.router'))
const ModelsRouter = lazy(() => import('../modules/models/router/page.router'))
const UsersRouter = lazy(() => import('../modules/users/router/page.router'))
const MeRouter = lazy(() => import('../modules/me/router/page.router'))
const ClinicsRouter = lazy(() => import('../modules/clinics/router/page.router'))
const DoctorsRouter = lazy(() => import('../modules/doctors/router/page.router'))
const OperationsRouter = lazy(() => import('../modules/operations/router/page.router'))
const CustomersRouter = lazy(() => import('../modules/customers/router/page.router'))
const GroupsRouter = lazy(() => import('../modules/groups/router/page.router'))
const DepartmentsRouter = lazy(() => import('../modules/departments/router/page.router'))
const WarehousesRouter = lazy(() => import('../modules/warehouses/router/page.router'))
const MeasurementsRouter = lazy(() => import('../modules/measurements/router/page.router'))
const SuppliersRouter = lazy(() => import('../modules/suppliers/router/page.router'))
const StocksRouter = lazy(() => import('../modules/stocks/router/page.router'))
const InsurancesRouter = lazy(() => import('../modules/insurances/router/page.router'))
const ProductsRouter = lazy(() => import('../modules/products/router/page.router'))
const ServicesRouter = lazy(() => import('../modules/services/router/page.router'))
const TransactionsRouter = lazy(() => import('../modules/transactions/router/page.router'))
const AppointmentsRouter = lazy(() => import('../modules/appointments/router/page.router'))
const PurchaseDocumentsRouter = lazy(() => import('../modules/purchase-documents/router/page.router'))
const CashRegisterRouter = lazy(() => import('../modules/cash-register/router/page.router'))
const CashflowRouter = lazy(() => import('../modules/cash-flow/cash-flow-overview/router/page.router'))
const CashboxesRouter = lazy(() => import('../modules/cash-flow/cashboxes/router/page.router'))
const CashCategoriesRouter = lazy(() => import('../modules/cash-flow/categories/router/page.router'))
const CashflowTransactionsRouter = lazy(() => import('../modules/cash-flow/transactions/router/page.router'))
const CashflowAnalyticsRouter = lazy(() => import('../modules/cash-flow/analytics/router/page.router'))
const StockAdjustmentsRouter = lazy(() => import('../modules/stock-adjustments/router/page.router'))
const StockMovementsRouter = lazy(() => import('../modules/stock-movements/router/page.router'))
const StockAdjustmentReasonsRouter = lazy(() => import('../modules/stock-adjustment-reasons/router/page.router'))
const ActivityLogsRouter = lazy(() => import('../modules/activity-logs/router/page.router'))
const RandevusRouter = lazy(() => import('../modules/randevus/router/page.router'))
const RandevusModalRouter = lazy(() => import('../modules/randevus/router/modal.router'))

// Modal Routers
const StatusModalRouter = lazy(() => import('../modules/status/router/modal.router'))
const UsersModalRouter = lazy(() => import('../modules/users/router/modal.router'))
const ClinicsModalRouter = lazy(() => import('../modules/clinics/router/modal.router'))
const OperationsModalRouter = lazy(() => import('../modules/operations/router/modal.router'))
const CustomersModalRouter = lazy(() => import('../modules/customers/router/modal.router'))
const GroupsModalRouter = lazy(() => import('../modules/groups/router/modal.router'))
const DepartmentsModalRouter = lazy(() => import('../modules/departments/router/modal.router'))
const WarehousesModalRouter = lazy(() => import('../modules/warehouses/router/modal.router'))
const MeasurementsModalRouter = lazy(() => import('../modules/measurements/router/modal.router'))
const SuppliersModalRouter = lazy(() => import('../modules/suppliers/router/modal.router'))
const InsurancesModalRouter = lazy(() => import('../modules/insurances/router/modal.router'))
const ProductsModalRouter = lazy(() => import('../modules/products/router/modal.router'))
const ServicesModalRouter = lazy(() => import('../modules/services/router/modal.router'))
const TransactionsModalRouter = lazy(() => import('../modules/transactions/router/modal.router'))
const PurchaseDocumentsModalRouter = lazy(() => import('../modules/purchase-documents/router/modal.router'))
const AppointmentsModalRouter = lazy(() => import('../modules/appointments/router/modal.router'))
const CashRegisterModalRouter = lazy(() => import('../modules/cash-register/router/modal.router'))
const CashboxesModalRouter = lazy(() => import('../modules/cash-flow/cashboxes/router/modal.router'))
const CashCategoriesModalRouter = lazy(() => import('../modules/cash-flow/categories/router/modal.router'))
const CashflowTransactionsModalRouter = lazy(() => import('../modules/cash-flow/transactions/router/modal.router'))
const StocksModalRouter = lazy(() => import('../modules/stocks/router/modal.router'))
const StockAdjustmentReasonsModalRouter = lazy(() => import('../modules/stock-adjustment-reasons/router/modal.router'))

export const MainRouter = () => {
  const { state } = useAuth()
  const location = useLocation()
  const { state: locationState } = location

  if (state.user.loading) {
    return <SuspenseSpin />
  }

  if (!!state.user.data) {
    return (
      <CashRegisterStatusProvider>
        <AppLayout>
          <Suspense fallback={<SuspenseSpin />}>
            <Routes location={locationState?.background || location}>
              <Route path='/statistics/*' element={<StatisticsRouter />} />
              <Route path='/status/*' element={<StatusRouter />} />
              <Route path='/models/*' element={<ModelsRouter />} />
              <Route path='/users/*' element={<UsersRouter />} />
              <Route path='/clinics/*' element={<ClinicsRouter />} />
              <Route path='/doctors/*' element={<DoctorsRouter />} />
              <Route path='/operations/*' element={<OperationsRouter />} />
              <Route path='/customers/*' element={<CustomersRouter />} />
              <Route path='/groups/*' element={<GroupsRouter />} />
              <Route path='/departments/*' element={<DepartmentsRouter />} />
              <Route path='/warehouses/*' element={<WarehousesRouter />} />
              <Route path='/measurements/*' element={<MeasurementsRouter />} />
              <Route path='/suppliers/*' element={<SuppliersRouter />} />
              <Route path='/stocks/*' element={<StocksRouter />} />
              <Route path='/insurances/*' element={<InsurancesRouter />} />
              <Route path='/products/*' element={<ProductsRouter />} />
              <Route path='/services/*' element={<ServicesRouter />} />
              <Route path='/appointments/*' element={<AppointmentsRouter />} />
              <Route path='/transactions/*' element={<TransactionsRouter />} />
              <Route path='/purchase-documents/*' element={<PurchaseDocumentsRouter />} />
              <Route path='/cash-register/*' element={<CashRegisterRouter />} />
              <Route path='/cash-flow/*' element={<CashflowRouter />} />
              <Route path='/cash-flow/cashboxes/*' element={<CashboxesRouter />} />
              <Route path='/cash-flow/cash-categories/*' element={<CashCategoriesRouter />} />
              <Route path='/cash-flow/transactions/*' element={<CashflowTransactionsRouter />} />
              <Route path='/cash-flow/analytics/*' element={<CashflowAnalyticsRouter />} />
              <Route path='/stock-adjustments/*' element={<StockAdjustmentsRouter />} />
              <Route path='/stock-movements/*' element={<StockMovementsRouter />} />
              <Route path='/stock-adjustment-reasons/*' element={<StockAdjustmentReasonsRouter />} />
              <Route path='/activity-logs/*' element={<ActivityLogsRouter />} />
              <Route path='/randevus/*' element={<RandevusRouter />} />
              <Route path='*' element={<Navigate to='/statistics' replace />} />
            </Routes>
            <Routes>
              <Route path='/status/*' element={<StatusModalRouter />} />
              <Route path='/users/*' element={<UsersModalRouter />} />
              <Route path='/clinics/*' element={<ClinicsModalRouter />} />
              <Route path='/operations/*' element={<OperationsModalRouter />} />
              <Route path='/operations/*' element={<OperationsModalRouter />} />
              <Route path='/customers/*' element={<CustomersModalRouter />} />
              <Route path='/groups/*' element={<GroupsModalRouter />} />
              <Route path='/departments/*' element={<DepartmentsModalRouter />} />
              <Route path='/warehouses/*' element={<WarehousesModalRouter />} />
              <Route path='/measurements/*' element={<MeasurementsModalRouter />} />
              <Route path='/suppliers/*' element={<SuppliersModalRouter />} />
              <Route path='/insurances/*' element={<InsurancesModalRouter />} />
              <Route path='/products/*' element={<ProductsModalRouter />} />
              <Route path='/services/*' element={<ServicesModalRouter />} />
              <Route path='/transactions/*' element={<TransactionsModalRouter />} />
              <Route path='/purchase-documents/*' element={<PurchaseDocumentsModalRouter />} />
              <Route path='/appointments/*' element={<AppointmentsModalRouter />} />
              <Route path='/cash-register/*' element={<CashRegisterModalRouter />} />
              <Route path='/cash-flow/cashboxes/*' element={<CashboxesModalRouter />} />
              <Route path='/cash-flow/cash-categories/*' element={<CashCategoriesModalRouter />} />
              <Route path='/cash-flow/transactions/*' element={<CashflowTransactionsModalRouter />} />
              <Route path='/stocks/*' element={<StocksModalRouter />} />
              <Route path='/stock-adjustment-reasons/*' element={<StockAdjustmentReasonsModalRouter />} />
              <Route path='/randevus/*' element={<RandevusModalRouter />} />
              <Route path='/similar-users' element={<SimilarUsers />} />
            </Routes>
          </Suspense>
        </AppLayout>
      </CashRegisterStatusProvider>
    )
  }

  return (
    <Suspense fallback={<SuspenseSpin />}>
      <Routes>
        <Route path='/*' element={<MeRouter />} />
      </Routes>
    </Suspense>
  )
}
